import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { paymentApi, couponApi } from '../services/api';
import toast from 'react-hot-toast';
import { Brain, ArrowRight, Loader2, Tag, AlertTriangle } from 'lucide-react';

function PaymentPage() {
  const { user } = useAuthStore();
  const navigate = useNavigate();

  const [feeData, setFeeData] = useState(null);
  const [feeLoading, setFeeLoading] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponDiscount, setCouponDiscount] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  useEffect(() => {
    if (user?.registrationPaid) {
      navigate('/chat', { replace: true });
      return;
    }
    loadFeeData();
  }, [user?.registrationPaid]);

  const loadFeeData = async () => {
    setFeeLoading(true);
    try {
      const res = await paymentApi.getRegistrationFee();
      setFeeData(res.data);
    } catch (error) {
      toast.error('Failed to load fee details');
    } finally {
      setFeeLoading(false);
    }
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim() || !feeData) return;
    setCouponLoading(true);
    try {
      const res = await couponApi.validate(couponCode.trim(), feeData.total);
      setCouponDiscount(res.data);
      toast.success(`Coupon applied! You save ₹${res.data.discount}`);
    } catch (error) {
      setCouponDiscount(null);
      toast.error(error.response?.data?.error || 'Invalid coupon code');
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = () => {
    setCouponCode('');
    setCouponDiscount(null);
  };

  const handlePayment = async () => {
    setPaymentProcessing(true);
    try {
      const orderRes = await paymentApi.createRegistrationOrder(
        couponDiscount ? couponCode.trim() : null
      );
      const { paymentUrl } = orderRes.data;
      window.location.href = paymentUrl;
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to initiate payment');
      setPaymentProcessing(false);
    }
  };

  const handleFreeTrial = async () => {
    try {
      navigate('/chat', { replace: true });
    } catch (error) {
      toast.error('Failed to start free trial');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-white flex flex-col items-center justify-center px-4 py-12">
      <div className="w-full max-w-lg">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Brain className="w-8 h-8 text-primary-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Complete Your Payment</h1>
          <p className="text-gray-500 mt-1">
            Welcome back, {user?.name || 'Student'}! Complete payment to access Memory Coach.
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-6">
          {feeLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-10 h-10 text-primary-600 animate-spin mb-4" />
              <p className="text-gray-600 font-medium">Loading fee details...</p>
            </div>
          ) : feeData ? (
            <>
              {/* Fee Breakdown Card */}
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-4">
                <h3 className="font-semibold text-gray-800 mb-3 text-sm">Fee Breakdown</h3>
                <div className="space-y-2 text-sm">
                  {feeData.trainerReferred ? (
                    <>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Registration Fee</span>
                        <span className="text-gray-800">₹{feeData.onboardingFee}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Starter Pack ({feeData.freeQuestions} Questions)</span>
                        <span className="text-gray-800">₹{feeData.starterPackBase}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">GST (18%)</span>
                        <span className="text-gray-800">₹{feeData.gst}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Service Fee</span>
                        <span className="text-gray-800">₹{feeData.serviceFee}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Platform Fee</span>
                        <span className="text-gray-800">₹{feeData.platformFee}</span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Course Fee</span>
                        <span className="text-gray-800">₹{feeData.courseFee?.toLocaleString('en-IN')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Service Fee</span>
                        <span className="text-gray-800">₹{feeData.serviceFee}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Platform Fee</span>
                        <span className="text-gray-800">₹{feeData.platformFee}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">GST</span>
                        <span className="text-gray-800">₹{feeData.gst}</span>
                      </div>
                      <div className="flex justify-between pt-1 border-t border-gray-200 mt-1">
                        <span className="text-gray-600">Starter Pack ({feeData.freeQuestions} Questions)</span>
                        <span className="text-gray-800">₹{feeData.starterPackBase}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Starter Pack GST (18%)</span>
                        <span className="text-gray-800">₹{feeData.starterPackGst}</span>
                      </div>
                    </>
                  )}
                  {couponDiscount && (
                    <div className="flex justify-between text-green-600">
                      <span>Coupon Discount</span>
                      <span>-₹{couponDiscount.discount}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-semibold pt-2 border-t border-gray-300 text-base">
                    <span>Total</span>
                    <span className="text-primary-700">₹{couponDiscount
                      ? couponDiscount.finalAmount.toLocaleString('en-IN')
                      : feeData.total.toLocaleString('en-IN')
                    }</span>
                  </div>
                </div>
              </div>

              {/* What you get */}
              <div className="bg-primary-50 border border-primary-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-primary-800 font-medium mb-1">What you get:</p>
                <ul className="text-sm text-primary-700 space-y-1">
                  <li>• Personalized memory techniques by Deepak Sir</li>
                  <li>• AI-powered memory coaching assistant</li>
                  <li>• Access to all memory techniques & courses</li>
                  <li>• Peg System, Memory Palace & more</li>
                  <li>• <strong>{feeData.freeQuestions} AI questions</strong> included (Starter Pack)</li>
                </ul>
              </div>

              {/* Coupon Section */}
              <div className="mb-4">
                {couponDiscount ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center justify-between">
                    <div className="flex items-center">
                      <Tag className="w-4 h-4 text-green-600 mr-2" />
                      <div>
                        <p className="text-green-700 font-medium text-sm">
                          "{couponCode}" applied!
                        </p>
                        <p className="text-xs text-green-600">
                          You save ₹{couponDiscount.discount}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={removeCoupon}
                      className="text-sm text-red-500 hover:text-red-600 font-medium"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2">
                    <div className="relative flex-1">
                      <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                        placeholder="Have a coupon code?"
                        className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                    <button
                      onClick={handleApplyCoupon}
                      disabled={couponLoading || !couponCode.trim()}
                      className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-100 rounded-lg text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 transition-colors"
                    >
                      {couponLoading ? 'Checking...' : 'Apply'}
                    </button>
                  </div>
                )}
              </div>

              {/* Pay Button */}
              <button
                onClick={() => { setShowPaymentModal(true); setAgreedToTerms(false); }}
                disabled={paymentProcessing}
                className="w-full py-3 bg-primary-600 text-white font-semibold rounded-xl hover:bg-primary-700 disabled:opacity-50 transition-colors flex items-center justify-center"
              >
                {paymentProcessing ? (
                  <><Loader2 className="w-5 h-5 animate-spin mr-2" /> Processing...</>
                ) : (
                  <>Pay ₹{couponDiscount
                    ? couponDiscount.finalAmount.toLocaleString('en-IN')
                    : feeData.total.toLocaleString('en-IN')
                  } <ArrowRight className="w-5 h-5 ml-2" /></>
                )}
              </button>

              <p className="text-center text-xs text-gray-400 mt-3">
                Secure payment powered by Instamojo
              </p>

              {/* Divider */}
              <div className="flex items-center my-4">
                <div className="flex-1 border-t border-gray-200"></div>
                <span className="px-3 text-xs text-gray-400">or</span>
                <div className="flex-1 border-t border-gray-200"></div>
              </div>

              {/* Free Trial Button */}
              <button
                onClick={handleFreeTrial}
                className="w-full py-3 bg-white dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 font-semibold rounded-xl hover:border-primary-400 hover:text-primary-600 dark:hover:border-primary-500 dark:hover:text-primary-400 transition-colors text-sm"
              >
                Start Free Trial Instead
              </button>
              <p className="text-center text-xs text-gray-400 mt-2">
                Try Memory Coach with limited daily questions for 7 days
              </p>
            </>
          ) : (
            <div className="text-center py-8">
              <p className="text-red-500 mb-2">Failed to load fee details.</p>
              <button
                onClick={loadFeeData}
                className="text-primary-600 hover:text-primary-700 font-medium text-sm"
              >
                Try again
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Payment Confirmation Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/40 rounded-full flex items-center justify-center mr-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Important</h3>
            </div>

            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 mb-4">
              <ul className="text-sm text-amber-800 dark:text-amber-200 space-y-2">
                <li>1. Please <strong>do not close the browser</strong> or press back during the payment process.</li>
                <li>2. After successful payment, you will be <strong>automatically redirected</strong> back to the website.</li>
                <li>3. Wait for the redirect to complete — <strong>do not navigate away</strong> until you see the confirmation.</li>
                <li>4. If payment gets stuck, contact support or the admin can recover it from the dashboard.</li>
              </ul>
            </div>

            <label className="flex items-start space-x-3 mb-5 cursor-pointer">
              <input
                type="checkbox"
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
                className="mt-0.5 w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-200">
                I have read the above instructions and agree to the{' '}
                <a href="/privacy-policy" target="_blank" className="text-primary-600 dark:text-primary-400 underline">Privacy Policy</a>
              </span>
            </label>

            <div className="flex space-x-3">
              <button
                onClick={() => setShowPaymentModal(false)}
                className="flex-1 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 font-medium rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowPaymentModal(false);
                  handlePayment();
                }}
                disabled={!agreedToTerms}
                className="flex-1 py-2.5 bg-primary-600 text-white font-medium rounded-xl hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm flex items-center justify-center"
              >
                Proceed to Pay <ArrowRight className="w-4 h-4 ml-1" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PaymentPage;
